import { useMenu, isLeaf } from './useMenu';
import { NavIcon } from './NavIcon';
import { RailSearch } from './RailSearch';
import { useOpenScreen } from '../tabs/useOpenScreen';
import { useTabsStore } from '../tabs/tabsStore';
import { Tooltip } from '@/shared/ui/Tooltip';
import { cn } from '@/shared/lib/cn';
import type { UserScreenNode } from '@/shared/types/system';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/shared/ui/DropdownMenu';

function holds(node: UserScreenNode, screenId: string | null): boolean {
  return !!screenId && node.children.some((c) => c.screenId === screenId || holds(c, screenId));
}

/**
 * Açılır panelin öğeleri. Alt klasörler AŞAĞI değil YANA açılır (#480): aşağı açılan ağaç
 * paneli uzatıp ekranın altına taşıyordu. Radix alt menüsü çapraz fare hareketinde paneli
 * kapatmıyor (menu aim), sığmayan tarafa kendiliğinden döner ve klavyeyi (→ ← Enter Esc) taşır.
 */
function FlyoutItems({
  nodes,
  activeScreenId,
  onOpen,
}: {
  nodes: UserScreenNode[];
  activeScreenId: string | null;
  onOpen: (n: UserScreenNode) => void;
}) {
  return (
    <>
      {nodes.map((c) =>
        isLeaf(c) ? (
          <DropdownMenuItem
            key={c.screenId}
            onSelect={() => onOpen(c)}
            className={cn(
              c.screenId === activeScreenId && 'bg-brand-500/10 text-brand-700 dark:text-brand-400',
            )}
          >
            <NavIcon node={c} className="h-4 w-4 shrink-0" />
            <span className="min-w-0 flex-1 truncate">{c.caption}</span>
          </DropdownMenuItem>
        ) : (
          <DropdownMenuSub key={c.screenId}>
            <DropdownMenuSubTrigger
              className={cn(
                'font-semibold',
                holds(c, activeScreenId) && 'text-brand-700 dark:text-brand-400',
              )}
            >
              <NavIcon node={c} className="h-4 w-4 shrink-0" />
              <span className="min-w-0 flex-1 truncate">{c.caption}</span>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="max-h-[80vh] w-64 overflow-y-auto">
              <FlyoutItems nodes={c.children} activeScreenId={activeScreenId} onOpen={onOpen} />
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        ),
      )}
    </>
  );
}

/** İkon rayı: kök düğümler ikon; klasörler yana açılan (cascade) menüyle. */
export function RailMenu() {
  const { tree } = useMenu();
  const openScreen = useOpenScreen();
  const activeTab = useTabsStore((s) => s.tabs.find((x) => x.tabId === s.activeTabId));
  const activeScreenId = activeTab?.screenId ?? null;
  return (
    <nav aria-label="Menü" className="flex flex-col items-center gap-1 py-2">
      {/* Daraltılmış menüde de arama (#497): genişletmeden yana açılan panel. */}
      <RailSearch />
      <div className="bg-border my-0.5 h-px w-8" aria-hidden />
      {tree.map((n) =>
        isLeaf(n) ? (
          <Tooltip key={n.screenId} content={n.caption} side="right">
            <button
              type="button"
              onClick={() => openScreen(n)}
              className="hover:bg-bg-hover rounded-md p-2"
              aria-label={n.caption}
            >
              <NavIcon node={n} className="h-5 w-5" />
            </button>
          </Tooltip>
        ) : (
          <DropdownMenu key={n.screenId}>
            <Tooltip content={n.caption} side="right">
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className={cn(
                    'hover:bg-bg-hover data-[state=open]:bg-bg-hover rounded-md p-2',
                    holds(n, activeScreenId) && 'bg-brand-500/10',
                  )}
                  aria-label={n.caption}
                >
                  <NavIcon node={n} className="h-5 w-5" />
                </button>
              </DropdownMenuTrigger>
            </Tooltip>
            <DropdownMenuContent
              side="right"
              align="start"
              sideOffset={8}
              className="max-h-[80vh] w-64 overflow-y-auto"
            >
              <DropdownMenuLabel>{n.caption}</DropdownMenuLabel>
              <FlyoutItems nodes={n.children} activeScreenId={activeScreenId} onOpen={openScreen} />
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      )}
    </nav>
  );
}
